import React, { useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";
import { Modal } from "react-bootstrap";
import BackModal from "../../BasicElements/BackModal";
import ConfirmModal from "../../BasicElements/ConfirmModal";
import http from "../../../Common/http-common";
import * as common from "../../../Common/common";
import * as Comp from "../../../Common/CommonComponents";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";

const ExpensesDetailsUpdate = (props) => {
    const [cookies] = useCookies([]);
    const [userId] = useState(cookies.USER_ID);
    const [userName] = useState(cookies.USERNAME);

    const [editData, setEditData] = useState({});
    const [inputError, setInputError] = useState({});
    const [showBackModal, setShowBackModal] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
		const [originalData, setOriginalData] = useState({});

		useEffect(() => {
				if (props.editData) {
						setEditData({
								...props.editData,
								DATE: props.editData.DATE
										? props.editData.DATE.split("T")[0]
										: ""
						});

						setHasChanges(false); // reset change flag
				}
		}, [props.editData]);

		useEffect(() => {
				if (props.editData) {
						const formatted = {
								...props.editData,
								DATE: props.editData.DATE
										? props.editData.DATE.split("T")[0]
										: ""
						};

						setEditData(formatted);
						setOriginalData(formatted);
						setHasChanges(false);
				}
		}, [props.editData]);

    const isMileage = !!(editData?.CATEGORY_TYPE_NAME?.toLowerCase().includes("mileage") || editData?.IS_MILEAGE);
    const isMedical = !!(editData?.CATEGORY_TYPE_NAME?.toLowerCase().includes("medical") || editData?.IS_MEDICAL);
    const mileageRate = parseFloat(editData?.MILEAGE_RATE || 0);

    // INPUT
    const inputHandler = (e) => {
        const { id, value } = e.target;

        const updated = {
						...editData,
						[id]: value
				};

				setEditData(updated);

				const hasDiff = JSON.stringify(updated) !== JSON.stringify(originalData);
				setHasChanges(hasDiff);

        if (!value || value.trim() === "") {
            setInputError((prev) => ({
                ...prev,
                [id]: `${id} cannot be empty`
            }));
        } else {
            setInputError((prev) => ({
                ...prev,
                [id]: ""
            }));
        }
    };

    // MILEAGE DISTANCE INPUT — auto-calculates RM amount
    const mileageInputHandler = (e) => {
        const val = e.target.value;
        if (!/^\d*\.?\d*$/.test(val)) return;

        const km = parseFloat(val || 0);
        const rm = km * mileageRate;

        const updated = {
            ...editData,
            MILEAGE: val,
            AMOUNT: rm.toFixed(2)
        };

        setEditData(updated);
        const hasDiff = JSON.stringify(updated) !== JSON.stringify(originalData);
        setHasChanges(hasDiff);

        if (!val || parseFloat(val) <= 0) {
            setInputError((prev) => ({ ...prev, MILEAGE: "Distance required" }));
        } else {
            setInputError((prev) => ({ ...prev, MILEAGE: "" }));
        }
    };

    // VALIDATION
    const validate = () => {
				let error = {};

				if (!editData.DATE)
						error.DATE = "Date required";

				if (isMileage) {
						if (!editData.MILEAGE || parseFloat(editData.MILEAGE) <= 0)
								error.MILEAGE = "Distance required";
				} else {
						if (!editData.AMOUNT || parseFloat(editData.AMOUNT) <= 0)
								error.AMOUNT = "Amount required";
				}

				if (!editData.EXPENSES_DESC)
						error.EXPENSES_DESC = "Description required";

				setInputError(error);
				return Object.keys(error).length === 0;
		};

    // SAVE
    const saveEditDetails = () => {
        let functionName = "EditExpensesDetails";

        if (!validate()) return;

				if (!hasChanges) {
						toast.warning("No changes detected.");
						return;
				}

        if (isMedical) {
            const entered = parseFloat(editData.AMOUNT || 0);
            const balance = props.medicalInfo?.balance || 0;

            if (entered > balance) {
                toast.error("Exceed medical claim balance!");
                return;
            }
        }

        const data = {
            EXPENSES_MST_ID: editData.EXPENSES_MST_ID,
            EXPENSES_DETAILS_ID: editData.EXPENSES_DETAILS_ID,
            CATEGORY_TYPE_MST_ID: editData.CATEGORY_TYPE_MST_ID,
            EXPENSES_DESC: editData.EXPENSES_DESC,
            AMOUNT: parseFloat(editData.AMOUNT),
            MILEAGE: isMileage ? parseFloat(editData.MILEAGE || 0) : 0,
            REMARK: editData.REMARK,
						DATE: editData.DATE ? editData.DATE + "T00:00:00" : null,
            USER_MST_GUID: userId,
            UPDATE_ID: userId,
            UPDATE_NAME: userName,
            FROM_SOURCE: { SOURCE: "WEB", MODULE_ID: props.module }
        };

        http.post("api/expenses/EditExpensesDetails", data)
            .then(() => {

                const updatedRow = {
                    ...editData,
                    AMOUNT: parseFloat(editData.AMOUNT)
                };

                props.onUpdateRow?.(updatedRow);

                toast.success("Expenses updated successfully");

                props.onReload();

                //CLOSE MODAL AFTER SAVE
                props.onHide();
            })
            .catch((err) => {
								const msg = err?.response?.data?.MESSAGE || "Failed to update expenses";
								toast.error(msg);
								common.c_LogWebError(props.page, functionName, err);
						});
    };

    return (
        <>
            <Modal show={props.show} onHide={props.onHide} centered size="xl">

                {/* HEADER SAME AS ALLOWANCE */}
                <Modal.Header>
									<div style={{ backgroundColor: "#FF8C00", width: "100%" }}>
											<Modal.Title style={{ textAlign: "left", marginLeft: "5px" }}>
													Edit
											</Modal.Title>
									</div>
							</Modal.Header>

                <Modal.Body>

                    <h5>Expenses Details</h5>
                    <hr />

                    <div className="allowanceTableWrapper">
                        <table className="allowanceTable">

                            {/* ROW 1 */}
                            <tr>

                                <td colSpan="3" style={{ paddingRight: "1rem" }}>
                                    <Comp.Input
																				id="DATE"
																				label="Date*"
																				type="date"
																				value={editData.DATE}
																				onChange={inputHandler}
																		/>
                                </td>

                                <td colSpan="5" style={{ paddingRight: "1rem" }}>
                                    <Comp.Input
                                        id="CATEGORY_TYPE_NAME"
                                        label="Type"
                                        value={editData.CATEGORY_TYPE_NAME}
                                        disabled
                                    />
                                    {isMedical && props.medicalInfo && (
                                        <div style={{ marginTop: "5px", fontSize: "13px", color: "#444" }}>
                                            Entitled: RM {(props.medicalInfo.entitled || 0).toFixed(2)} |&nbsp;
                                            Claimed: RM {(props.medicalInfo.claimed || 0).toFixed(2)} |&nbsp;
                                            Balance: <b>RM {(props.medicalInfo.balance || 0).toFixed(2)}</b>
                                        </div>
                                    )}
                                    {isMileage && (
                                        <div style={{ marginTop: "5px", fontSize: "13px", color: "#444" }}>
                                            Rate: <b>RM {mileageRate.toFixed(2)} per KM</b>
                                        </div>
                                    )}
                                </td>

                                <td colSpan="3">
                                    {isMileage ? (
                                        <Comp.Input
                                            id="MILEAGE"
                                            label="Distance (KM)*"
                                            value={editData.MILEAGE || ""}
                                            onChange={mileageInputHandler}
                                            errorMessage={inputError.MILEAGE}
                                        />
                                    ) : (
                                        <Comp.Input
                                            id="AMOUNT"
                                            label="Amount (RM)*"
                                            value={editData.AMOUNT}
                                            onChange={inputHandler}
                                            errorMessage={inputError.AMOUNT}
                                        />
                                    )}
                                </td>

                            </tr>

                            {/* ROW 2 */}
                            <tr>

                                <td colSpan="6" style={{ paddingRight: "1rem" }}>
                                    <label>Description*</label>
                                    <textarea
                                        id="EXPENSES_DESC"
                                        rows={2}
                                        className="form-control-allowance"
                                        value={editData.EXPENSES_DESC || ""}
                                        onChange={inputHandler}
                                    />
                                    {inputError.EXPENSES_DESC && (
                                        <div className="text-danger">
                                            {inputError.EXPENSES_DESC}
                                        </div>
                                    )}
                                </td>

                                <td colSpan="6">
                                    <label>Remark</label>
                                    <textarea
                                        value={editData.REMARK || ""}
                                        readOnly
                                        className="form-control-allowance"
                                        style={{ background: "#f5f5f5" }}
                                    />
                                </td>

                            </tr>

                        </table>
                    </div>

                </Modal.Body>

                <Modal.Footer>
									<div className="onright-container">

										<Comp.Button
											id="btnCancel"
											type="cancel"
											onClick={props.onHide}
										>
											<FontAwesomeIcon icon={faXmark} />&nbsp; Cancel
										</Comp.Button>

										<Comp.Button
											id="btnSave"
											onClick={() => {
													if (!hasChanges) {
															toast.warning("No changes detected.");
															return;
													}
													setShowConfirmModal(true);
											}}
										>
											<FontAwesomeIcon icon={faCheck} />&nbsp; Save
										</Comp.Button>

									</div>
								</Modal.Footer>

            </Modal>

            {/* BACK MODAL */}
            {/* <BackModal
                show={showBackModal}
                onHide={() => setShowBackModal(false)}
                onConfirm={() => props.onHide()}
            /> */}

            {/* CONFIRM MODAL */}
            <ConfirmModal
							show={showConfirmModal}
							onHide={() => setShowConfirmModal(false)}
							onConfirm={() => {
								setShowConfirmModal(false);
								saveEditDetails();
							}}
							prop="Confirm to save"
						>
							Are you sure you want to update expenses details?
						</ConfirmModal>
        </>
    );
};

export default ExpensesDetailsUpdate;